/**
 * Screen 3 of 4 — VALUE & PROVENANCE.
 *
 * Shows the band and then shows its working. Two rules govern this screen:
 *
 *   1. The deliverable is a BAND, never a bare point estimate. The engine's
 *      `point_estimate` appears only inside the triangulation panel, as an
 *      input to the band — never as the headline number.
 *   2. `positioning` is rendered verbatim. It is the AICPA SSVS No. 1 sentence
 *      that distinguishes a calculated value from a conclusion of value; it is
 *      not ours to paraphrase into something friendlier.
 *
 * On the missing-provenance case: the API persists only the band on the
 * engagement row. The full disclosure — which signed profile, which multiple,
 * which overrides — exists in the run response and nowhere else. If this screen
 * is opened without that response in the session cache (new tab, later day),
 * it shows the persisted band and says plainly that the provenance requires a
 * re-run. It does not reconstruct one.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { getValuationEngagement } from '@/api/valuation'
import { formatApiError } from '@/utils/httpErrors'
import { useAuth } from '@/contexts/AuthContext'
import type { ValuationEngagement } from '@/types/valuation'
import {
  StatusBadge,
  basisLabel,
  ValuationStepper,
  ValueKindBadge,
  useCachedRun,
} from './_shared'
import { money } from '@/utils/format'
import { nextStep } from './_nextStep'
import { convergenceLabel } from './_convergenceLabel'
import {
  ApproachesPanel,
  BenchmarkPanel,
  ComparableEvidencePanel,
  EarningsAndRatePanel,
  OverridesPanel,
  TriangulationPanel,
} from './_OutputProvenanceSigned'

export default function ValuationOutput() {
  const { valuationId } = useParams<{ valuationId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const run = useCachedRun(valuationId)

  const [engagement, setEngagement] = useState<ValuationEngagement | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    if (!valuationId) return
    let live = true
    getValuationEngagement(valuationId)
      .then(en => { if (live) setEngagement(en) })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
    return () => { live = false }
  }, [valuationId])

  if (loadError) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{loadError}</div>
      </div>
    )
  }
  if (!engagement) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <div>
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-2 h-4 w-40" />
        </div>
        <Skeleton className="h-10 w-full" />
        <Card>
          <CardHeader><Skeleton className="h-5 w-40" /></CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-8 w-56" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader><Skeleton className="h-5 w-40" /></CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </CardContent>
        </Card>
      </div>
    )
  }

  const concluded = engagement.value_kind === 'concluded'
  const persistedLow = concluded ? engagement.concluded_value_low : engagement.calculated_value_low
  const persistedHigh = concluded ? engagement.concluded_value_high : engagement.calculated_value_high
  const low = run?.value_low ?? persistedLow
  const high = run?.value_high ?? persistedHigh
  const hasBand = low !== null && low !== undefined && high !== null && high !== undefined
  // R5: disabled until a value exists; the reason is the same nextStep()
  // answer the rest of this brief uses ("Continue intake"/"Start intake").
  const step = nextStep(engagement, user?.role)

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{engagement.subject_company_name}</h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500">
          <ValueKindBadge kind={engagement.value_kind} />
          <StatusBadge status={engagement.status} />
          <span className="text-xs">{engagement.industry_label ?? engagement.industry ?? 'no industry set'}</span>
          {engagement.period_label && <span>· {engagement.period_label}</span>}
        </div>
      </div>

      <ValuationStepper current="output" engagementId={valuationId} />

      {!hasBand ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">
              No value has been calculated for this engagement yet.
            </p>
            <Button className="mt-4" onClick={() => navigate(`/valuation/${valuationId}/intake`)}>
              Go to intake
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>{concluded ? 'Conclusion of value' : 'Calculated value'}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold tracking-tight text-[#0d1f3c]">
              {money(low)} <span className="text-gray-400">–</span> {money(high)}
            </p>
            <p className="mt-3 text-sm text-gray-700">
              {run?.positioning ?? (concluded
                ? 'Conclusion of value (AICPA SSVS No. 1 valuation engagement).'
                : 'Calculated value (AICPA SSVS No. 1 calculation engagement) — NOT a conclusion of value.')}
            </p>
            {run && (
              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <span className="rounded bg-gray-100 px-2 py-1 font-mono text-gray-600">
                  {basisLabel(run.computed_range.basis)}
                </span>
                <span
                  className={`rounded px-2 py-1 font-mono ${
                    run.convergence_status === 'converged'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-amber-100 text-amber-900'
                  }`}
                >
                  {convergenceLabel(run.convergence_status)}
                </span>
              </div>
            )}
            {run && (run.convergence_status.startsWith('divergent') || run.override_out_of_range || run.override_unresolved) && (
              <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                This run requires Valuation Signer review — the approaches did not converge inside the signed
                spread, or an override landed outside its signed band. It is not sign-off ready
                until a credentialed Valuation Signer has resolved that.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {hasBand && !run && (
        <div className="rounded-lg border border-gray-300 bg-gray-50 p-4 text-sm text-gray-700">
          <p className="font-medium text-gray-900">Provenance not available in this session</p>
          <p className="mt-1">
            The band above is the value stored on the engagement. The supporting disclosure —
            signed profile, selected multiple, build-up rate, overrides — is produced by the run
            and is not stored alongside it. Re-run the valuation from intake to see the full
            working.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => navigate(`/valuation/${valuationId}/intake`)}
          >
            Re-run from intake
          </Button>
        </div>
      )}

      {run && (
        <>
          <BenchmarkPanel run={run} />
          <EarningsAndRatePanel run={run} />
          <ApproachesPanel run={run} />
          <TriangulationPanel run={run} />
          <ComparableEvidencePanel run={run} />
          <OverridesPanel run={run} />
        </>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => navigate(`/valuation/${valuationId}/intake`)}>
          Back to intake
        </Button>
        <div className="flex flex-col items-end gap-1">
          <Button
            onClick={() => navigate(`/valuation/${valuationId}/signoff`)}
            disabled={!hasBand}
            title={!hasBand ? step.label : undefined}
          >
            Sign-off state
          </Button>
          {!hasBand && <p className="text-xs text-gray-500">{step.label} first</p>}
        </div>
      </div>
    </div>
  )
}
